import type { ComponentType } from 'react';
import { lazy } from 'react';
import { useIntl } from 'react-intl';
import { Route, Routes } from 'react-router-dom';
import NotFoundPage from 'components/pages/not-found';
import AppRoute from 'components/routes/app-route';
import WebsiteLogin from 'components/pages/login/website-login';
import LoginPage from 'components/pages/login/login-page';
import LogoutPage from 'components/pages/login/logout-page';
import { invitationRoutes, onboardingRoutes, routes } from 'components/routes/routes';
import MainLayout from 'components/layout/main-layout';
import InnerLayout from 'components/layout/inner-layout';
import FaviconSwitcher from 'components/common/favicon-switcher';

const pageModules = import.meta.glob('./pages/**/*.tsx') as Record<string, () => Promise<{ default: ComponentType }>>;

const LazyComponent = (pagePath: string) =>
  lazy(() => {
    const loader = pageModules[`./pages/${pagePath}.tsx`];
    if (!loader) throw new Error(`Page module not found: ${pagePath}`);
    return loader();
  });
export const AppSwitch = () => {
  const intl = useIntl();

  // login pages should not be lazy loaded
  return (
    <Routes>
      <Route path="/login/website" element={<AppRoute LazyComponent={WebsiteLogin} isPublic />} />
      <Route path="/login" element={<AppRoute LazyComponent={LoginPage} isPublic />} />
      <Route path="/logout" element={<AppRoute LazyComponent={LogoutPage} isPublic />} />
      {invitationRoutes.map(({ layout: Layout, ...route }) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <AppRoute LazyComponent={LazyComponent(route.page)} isPublic>
              {content => <Layout>{content}</Layout>}
            </AppRoute>
          }
        />
      ))}
      {onboardingRoutes.map(({ layout: Layout, ...route }) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <AppRoute LazyComponent={LazyComponent(route.page)}>{content => <Layout>{content}</Layout>}</AppRoute>
          }
        />
      ))}
      <Route
        path="*"
        element={
          <MainLayout>
            <Routes>
              {routes.map(route => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    <AppRoute
                      LazyComponent={LazyComponent(route.page)}
                      rbacPermissions={route.requiredPermissions}
                      permissionOperator={route.permissionOperator}
                      permissionProjectScope={route.permissionProjectScope}>
                      {content => (
                        <InnerLayout
                          title={intl.formatMessage({ id: route.title })}
                          breadcrumbs={route.breadcrumbs}
                          container={route.container}>
                          <FaviconSwitcher key={route.path} />
                          {content}
                        </InnerLayout>
                      )}
                    </AppRoute>
                  }
                />
              ))}
              <Route
                path="*"
                element={
                  <AppRoute LazyComponent={NotFoundPage}>
                    {content => (
                      <InnerLayout title={intl.formatMessage({ id: 'not-found.title' })}>{content}</InnerLayout>
                    )}
                  </AppRoute>
                }
              />
            </Routes>
          </MainLayout>
        }
      />
    </Routes>
  );
};
